import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

// others
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { EntryStatus } from '../../../generated'
import { ENTRIES_SORT_VALUES, FILTER_DATE_FORMAT, QUERY_PARAMS, STANDARD_DATE_FORMAT } from '../../constants'
import { useCategories } from '../../hooks/useCategories'
import { StyledInputLabel } from '../../theme/styleComponents'
import { anchorOrigin, transformOrigin } from '../../theme/styleConstants'
import MatterDropdown from '../common/MatterDropdown'
import UserFilter from './UserFilter'
import LegalTeamUserFilter from './LegalTeamUserFilter'
import { EntryFiltersProps } from '../../interfaceTypes/props'

const EntryFilters = ({
  isNonClientEntry,
  onRefresh,
  loading,
  hideMatter,
  entryType,
  legalTeamUsers
}: EntryFiltersProps) => {
  const { categories } = useCategories({})
  const { type: routeType } = useParams()
  const type = entryType ?? routeType

  // const { isAdmin, isParalegal } = useContext(AuthContext)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const mediumViewport = useMediaQuery('(min-width:929px)')

  const toggleDrawer = (open: boolean) => setDrawerOpen(open)

  const getFilterValues = (key: QUERY_PARAMS) => searchParams.get(key) || QUERY_PARAMS.ALL

  const setParams = (key: QUERY_PARAMS, value: string) => {
    if (value) searchParams.set(key, value)
    else {
      searchParams.delete(QUERY_PARAMS.STATUS)
      searchParams.delete(QUERY_PARAMS.MATTER)
      searchParams.delete(QUERY_PARAMS.USER)
      searchParams.delete(QUERY_PARAMS.KEYWORD)
    }
    setSearchParams(searchParams)
  }

  const validEntriesSortValues: string[] = [
    ENTRIES_SORT_VALUES.MOST_RECENT,
    ENTRIES_SORT_VALUES.ACTIVITY_NEWEST,
    ENTRIES_SORT_VALUES.ACTIVITY_OLDEST
  ]
  const entriesSortParam = searchParams.get(QUERY_PARAMS.ENTRIES_SORT)
  const entriesSort =
    entriesSortParam && validEntriesSortValues.includes(entriesSortParam)
      ? entriesSortParam
      : ENTRIES_SORT_VALUES.MOST_RECENT

  const FilterContent = () => {
    return (
      <>
        <DateRanges
          setParams={setParams}
          getFilterValues={getFilterValues}
          mediumViewport={mediumViewport}
          smallTextField={false}
        />

        <Box
          flex={0.75}
          my={mediumViewport ? 0 : 3}
          gap={mediumViewport ? 2 : 5}
          display={'flex'}
          flexDirection={mediumViewport ? 'row' : 'column'}
          alignItems={'center'}
          justifyContent={isNonClientEntry ? 'flex-end' : 'space-evenly'}
        >
          <Box
            flex={isNonClientEntry ? 0.5 : 1}
            width={mediumViewport ? '100%' : '100%'}
            minWidth={mediumViewport ? 160 : 0}
          >
            <FormControl variant="outlined" fullWidth>
              <Box flex={1} width={mediumViewport ? 'auto' : '100%'}>
                {legalTeamUsers ? (
                  <LegalTeamUserFilter
                    users={legalTeamUsers}
                    setParams={setParams}
                    getFilterValues={getFilterValues}
                    label="Legal Person"
                  />
                ) : (
                  <UserFilter setParams={setParams} getFilterValues={getFilterValues} label="Legal Person" />
                )}
              </Box>
            </FormControl>
          </Box>

          {!isNonClientEntry && (
            <Box flex={0.5} width={mediumViewport ? 'auto' : '100%'} minWidth={mediumViewport ? 120 : 0}>
              <FormControl variant="outlined" fullWidth>
                <StyledInputLabel id="entryStatus-label">Status</StyledInputLabel>

                <Select
                  labelId="entryStatus-label"
                  label="Status"
                  id="entryStatus-select"
                  displayEmpty={true}
                  defaultValue={'all'}
                  sx={{ width: '100%' }}
                  MenuProps={{ anchorOrigin, transformOrigin }}
                  onChange={event => setParams(QUERY_PARAMS.STATUS, event.target.value as string)}
                  value={getFilterValues(QUERY_PARAMS.STATUS)}
                >
                  <MenuItem value={QUERY_PARAMS.ALL}>All</MenuItem>
                  {Object.entries(EntryStatus)?.map(([key, value]) => (
                    <MenuItem value={key} key={key}>
                      {value.at(0) + value.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {!isNonClientEntry && !hideMatter && (
            <Box flex={1} width={mediumViewport ? 'auto' : '100%'}>
              <FormControl variant="outlined" fullWidth>
                <Box flex={1} width={mediumViewport ? 'auto' : '100%'}>
                  <MatterDropdown setParams={setParams} getFilterValues={getFilterValues} label="Matter" />
                </Box>
              </FormControl>
            </Box>
          )}

          {type === 'expense' && (
            <Box flex={0.75} width={mediumViewport ? 'auto' : '100%'}>
              <FormControl variant="outlined" fullWidth>
                <StyledInputLabel id="categories-label">Categories</StyledInputLabel>

                <Select
                  label="Categories"
                  labelId="categories-label"
                  id="categories-select"
                  displayEmpty={true}
                  defaultValue={'all'}
                  sx={{ width: '100%' }}
                  MenuProps={{ anchorOrigin, transformOrigin }}
                  onChange={event => setParams(QUERY_PARAMS.CATEGORY, event.target.value as string)}
                  value={getFilterValues(QUERY_PARAMS.CATEGORY)}
                >
                  <MenuItem value={QUERY_PARAMS.ALL}>All</MenuItem>
                  {categories?.map(category => (
                    <MenuItem value={category?.id} key={category?.id}>
                      {`${category?.name || ''}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box flex={0.75} width={mediumViewport ? 'auto' : '100%'} minWidth={mediumViewport ? 170 : 0}>
            <FormControl variant="outlined" fullWidth>
              <StyledInputLabel id="entries-sort-label">Sort by</StyledInputLabel>
              <Select
                labelId="entries-sort-label"
                label="Sort by"
                id="entries-sort-select"
                displayEmpty={true}
                sx={{ width: '100%' }}
                MenuProps={{ anchorOrigin, transformOrigin }}
                onChange={event => setParams(QUERY_PARAMS.ENTRIES_SORT, event.target.value as string)}
                value={entriesSort}
              >
                <MenuItem value={ENTRIES_SORT_VALUES.MOST_RECENT}>Most recent</MenuItem>
                <MenuItem value={ENTRIES_SORT_VALUES.ACTIVITY_NEWEST}>Activity date (newest)</MenuItem>
                <MenuItem value={ENTRIES_SORT_VALUES.ACTIVITY_OLDEST}>Activity date (oldest)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {onRefresh && (
            <Box display="flex" alignItems="center">
              <Tooltip title="Refresh to load latest">
                <IconButton onClick={onRefresh} disabled={loading} size="small" aria-label="Refresh entries">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </>
    )
  }

  return (
    <Box
      mx={2}
      my={4}
      display={'flex'}
      flexWrap={'wrap'}
      gap={2}
      justifyContent={mediumViewport ? 'space-between' : 'end'}
      alignItems="center"
    >
      {mediumViewport ? (
        <FilterContent />
      ) : (
        <>
          <IconButton title="Show filters" onClick={() => toggleDrawer(true)} aria-label="Show filters">
            <FilterListIcon color="primary" />
          </IconButton>

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => toggleDrawer(false)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 'auto' } } }}
          >
            <Box position="relative" top={63} width={'100%'} minWidth={{ xs: 0, sm: 380 }} p={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Time Entry Filters</Typography>

                <IconButton onClick={() => toggleDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <FilterContent />
            </Box>
          </Drawer>
        </>
      )}
    </Box>
  )
}

export const DateRanges = ({
  setParams,
  getFilterValues,
  mediumViewport,
  smallTextField = true,
  fullWidth = false
}: {
  setParams: (key: QUERY_PARAMS, value: string) => void
  getFilterValues: (key: QUERY_PARAMS) => string
  mediumViewport: boolean
  smallTextField?: boolean
  // Stretches both pickers to share the container's width (e.g. a grid cell).
  fullWidth?: boolean
}) => {
  const getDate = (isFrom = true) => dayjs(getFilterValues(isFrom ? QUERY_PARAMS.FROM : QUERY_PARAMS.TO))

  const datePickerProps = {
    inputFormat: STANDARD_DATE_FORMAT,
    renderInput: (params: TextFieldProps) => (
      <TextField
        size={smallTextField ? 'small' : 'medium'}
        {...params}
        sx={fullWidth ? { flex: 1, minWidth: 0 } : undefined}
      />
    )
  }

  return (
    <Box
      display="flex"
      gap="10px"
      alignItems={'center'}
      mt={mediumViewport ? 0 : 5}
      width={fullWidth ? '100%' : undefined}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...datePickerProps}
          label="From"
          value={getDate().isValid() ? getDate() : dayjs(new Date()).subtract(1, 'month').format(FILTER_DATE_FORMAT)}
          onChange={value => setParams(QUERY_PARAMS.FROM, dayjs(value!).format(FILTER_DATE_FORMAT))}
          maxDate={
            getDate(false).isValid()
              ? getDate(false).format(STANDARD_DATE_FORMAT)
              : dayjs(new Date()).format(FILTER_DATE_FORMAT)
          }
        />
      </LocalizationProvider>
      -
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...datePickerProps}
          label="To"
          value={getDate(false).isValid() ? getDate(false) : dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)}
          onChange={value => setParams(QUERY_PARAMS.TO, dayjs(value!).format(FILTER_DATE_FORMAT))}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default EntryFilters
